import React, { useState } from "react";

// Helper to compute SVG donut slice path with rounded angles & gaps
function getSlicePath(cx, cy, r1, r2, startAngleDeg, endAngleDeg, gapDeg = 2.5) {
  const angleSpan = endAngleDeg - startAngleDeg;
  // If slice is almost 360 degrees, ignore gap
  const actualGap = angleSpan >= 350 ? 0 : gapDeg;
  const effectiveStart = startAngleDeg + actualGap / 2;
  const effectiveEnd = endAngleDeg - actualGap / 2;

  if (effectiveEnd <= effectiveStart) return "";

  const startRad = ((effectiveStart - 90) * Math.PI) / 180;
  const endRad = ((effectiveEnd - 90) * Math.PI) / 180;

  const x1_out = cx + r2 * Math.cos(startRad);
  const y1_out = cy + r2 * Math.sin(startRad);
  const x2_out = cx + r2 * Math.cos(endRad);
  const y2_out = cy + r2 * Math.sin(endRad);

  const x1_in = cx + r1 * Math.cos(endRad);
  const y1_in = cy + r1 * Math.sin(endRad);
  const x2_in = cx + r1 * Math.cos(startRad);
  const y2_in = cy + r1 * Math.sin(startRad);

  const largeArc = effectiveEnd - effectiveStart > 180 ? 1 : 0;

  return `M ${x1_out} ${y1_out} A ${r2} ${r2} 0 ${largeArc} 1 ${x2_out} ${y2_out} L ${x1_in} ${y1_in} A ${r1} ${r1} 0 ${largeArc} 0 ${x2_in} ${y2_in} Z`;
}

const COLOR_MAP = {
  Permanent: { main: "#2563eb", bg: "bg-blue-500", lightBg: "bg-blue-50", text: "text-blue-700" },
  Contract: { main: "#10b981", bg: "bg-emerald-500", lightBg: "bg-emerald-50", text: "text-emerald-700" },
  Probation: { main: "#8b5cf6", bg: "bg-purple-500", lightBg: "bg-purple-50", text: "text-purple-700" },
  Intern: { main: "#f59e0b", bg: "bg-amber-500", lightBg: "bg-amber-50", text: "text-amber-700" },
};

const EmploymentTypeDonut = ({ data = [] }) => {
  const [hoveredIndex, setHoveredIndex] = useState(null);

  const total = data.reduce((sum, s) => sum + s.value, 0);

  // Compute slice angles
  let currentAngle = 0;
  const slices = data.map((item, index) => {
    const angleSpan = total > 0 ? (item.value / total) * 360 : 0;
    const startAngle = currentAngle;
    const endAngle = currentAngle + angleSpan;
    currentAngle = endAngle;

    const colors = COLOR_MAP[item.name] || {
      main: item.color || "#64748b",
      bg: "bg-slate-500",
      lightBg: "bg-slate-50",
      text: "text-slate-700",
    };

    return {
      ...item,
      index,
      startAngle,
      endAngle,
      colors,
    };
  });

  const activeItem = hoveredIndex !== null ? slices[hoveredIndex] : null;

  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-5 flex flex-col justify-between h-full min-h-[320px]">
      {/* Header */}
      <div className="mb-3 flex items-center justify-between">
        <h3 className="text-sm font-semibold text-slate-800">
          Employees by Employment Type
        </h3>
        <span className="text-[11px] font-medium text-slate-400 bg-slate-100 px-2 py-0.5 rounded-md">
          Distribution
        </span>
      </div>

      {data.length > 0 ? (
        <div className="grid grid-cols-1 sm:grid-cols-12 gap-4 items-center">
          {/* SVG Donut Chart */}
          <div className="sm:col-span-5 flex items-center justify-center relative">
            <svg
              width="170"
              height="170"
              viewBox="0 0 180 180"
              className="drop-shadow-sm transition-all duration-300"
            >
              {slices.map((slice) => {
                const isHovered = hoveredIndex === slice.index;
                const r1 = 52;
                const r2 = isHovered ? 82 : 77;
                const pathD = getSlicePath(90, 90, r1, r2, slice.startAngle, slice.endAngle, 3);

                return (
                  <path
                    key={slice.name}
                    d={pathD}
                    fill={slice.colors.main}
                    opacity={hoveredIndex === null || isHovered ? 1 : 0.45}
                    className="transition-all duration-200 cursor-pointer"
                    onMouseEnter={() => setHoveredIndex(slice.index)}
                    onMouseLeave={() => setHoveredIndex(null)}
                    style={{
                      transformOrigin: "90px 90px",
                      filter: isHovered ? "drop-shadow(0 4px 6px rgba(0,0,0,0.15))" : "none",
                    }}
                  />
                );
              })}
            </svg>

            {/* Center Dynamic Label */}
            <div
              className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none text-center"
              style={{ width: 100, height: 100, margin: "auto" }}
            >
              {activeItem ? (
                <>
                  <p className="text-xl font-extrabold text-slate-900 leading-none">
                    {activeItem.value.toLocaleString()}
                  </p>
                  <p
                    className="text-[10px] font-semibold truncate max-w-[80px] mt-1"
                    style={{ color: activeItem.colors.main }}
                  >
                    {activeItem.name}
                  </p>
                  <p className="text-[9px] text-slate-400 font-medium">
                    {activeItem.percentage}%
                  </p>
                </>
              ) : (
                <>
                  <p className="text-2xl font-black tracking-tight text-slate-800 leading-none">
                    {total.toLocaleString()}
                  </p>
                  <p className="text-[10px] text-slate-400 font-semibold uppercase tracking-wider mt-1">
                    Total
                  </p>
                </>
              )}
            </div>
          </div>

          {/* Interactive Legend List */}
          <div className="sm:col-span-7 flex flex-col gap-2.5">
            {slices.map((slice) => {
              const isHovered = hoveredIndex === slice.index;

              return (
                <div
                  key={slice.name}
                  onMouseEnter={() => setHoveredIndex(slice.index)}
                  onMouseLeave={() => setHoveredIndex(null)}
                  className={`group flex flex-col gap-1.5 p-2 rounded-xl border transition-all duration-200 cursor-pointer ${isHovered
                      ? "border-slate-300 bg-slate-50/80 shadow-sm translate-x-0.5"
                      : "border-transparent hover:bg-slate-50/50"
                    }`}
                >
                  <div className="flex items-center justify-between">
                    {/* Dot + Label */}
                    <div className="flex items-center gap-2">
                      <span
                        className="h-2.5 w-2.5 rounded-full shrink-0 transition-transform duration-200 group-hover:scale-125"
                        style={{ backgroundColor: slice.colors.main }}
                      />
                      <span className="text-xs font-semibold text-slate-700 group-hover:text-slate-900 transition-colors">
                        {slice.name}
                      </span>
                    </div>

                    {/* Count + Badge */}
                    <div className="flex items-center gap-2">
                      <span className="text-xs font-bold text-slate-800">
                        {slice.value.toLocaleString()}
                      </span>
                      <span
                        className={`text-[10px] font-bold px-1.5 py-0.5 rounded-md ${slice.colors.lightBg} ${slice.colors.text}`}
                      >
                        {slice.percentage}%
                      </span>
                    </div>
                  </div>

                  {/* Progress bar */}
                  <div className="h-1.5 w-full rounded-full bg-slate-100 overflow-hidden">
                    <div
                      className="h-full rounded-full transition-all duration-500"
                      style={{
                        width: `${slice.percentage}%`,
                        backgroundColor: slice.colors.main,
                        opacity: isHovered ? 1 : 0.85,
                      }}
                    />
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      ) : (
        <div className="flex h-40 items-center justify-center">
          <p className="text-sm text-slate-400">No employment type data</p>
        </div>
      )}
    </div>
  );
};

export default EmploymentTypeDonut;
